import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { facilitiesAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Calendar, Trophy, ArrowRight, Clock } from 'lucide-react';
import { formatDate, formatTime } from '@/lib/utils';

export const FacilityUpcomingMatches = ({ facilityId, title = 'Prossime partite qui' }) => {
  const [loading, setLoading] = useState(true);
  const [matches, setMatches] = useState([]);
  const [leagues, setLeagues] = useState([]);

  useEffect(() => {
    if (!facilityId) return;
    let cancelled = false;
    facilitiesAPI
      .getUpcomingMatches(facilityId)
      .then((res) => {
        if (cancelled) return;
        setMatches(res.data?.matches || []);
        setLeagues(res.data?.leagues || []);
      })
      .catch(() => {
        if (!cancelled) {
          setMatches([]);
          setLeagues([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [facilityId]);

  if (loading) {
    return <Skeleton className="h-40" />;
  }

  if (matches.length === 0 && leagues.length === 0) {
    return null;
  }

  return (
    <Card className="overflow-hidden">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Calendar className="h-4 w-4 text-primary" />
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {matches.length > 0 && (
          <div className="space-y-2">
            {matches.slice(0, 5).map((m) => (
              <Link
                key={m.match_id}
                to={`/matches/${m.match_id}`}
                className="flex items-center justify-between gap-3 p-2.5 rounded-lg hover:bg-muted/60 transition-colors"
              >
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">{m.title}</p>
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-muted-foreground mt-0.5">
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {formatDate(m.date_time, { day: 'numeric', month: 'short' })} · {formatTime(m.date_time)}
                    </span>
                    {m.league?.name && (
                      <span className="truncate">· {m.league.name}</span>
                    )}
                  </div>
                </div>
                <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0" />
              </Link>
            ))}
          </div>
        )}

        {leagues.length > 0 && (
          <div className="pt-3 border-t">
            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-1.5">
              <Trophy className="h-3 w-3" />
              Leghe che giocano qui
            </p>
            <div className="flex flex-wrap gap-2">
              {leagues.map((lg) => (
                <Link
                  key={lg.league_id}
                  to={`/leagues/${lg.league_id}`}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-muted hover:bg-primary/15 hover:text-primary transition-colors text-xs font-medium"
                >
                  <Trophy className="h-3 w-3" />
                  {lg.name}
                </Link>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default FacilityUpcomingMatches;
